<template>
  <div class="screen-organize-when screen-organize">
    <app-bar
      :title="
        event.groupId
          ? 'Nieuwe groepsactiviteit 1 / 5'
          : 'Nieuwe activiteit 3 / 7'
      "
      :show-back-button="true"
      :back-button-path="origin ? origin : '/events/new/type'"
      :show-profile-button="false"
    />
    <div class="container">
      <h1>Wanneer?</h1>

      <date-time-picker
        :disabled="false"
        title="Datum"
        :date-time="event.startDate"
        :start-date-time="event.startTime"
        :end-date-time="event.endTime"
        @change-date-time="changeDateTime"
      />

      <toggle
        v-model="isRecurring"
        title="Terugkerende activiteit"
        class="my-6"
        @toggle="
          (val) => {
            isRecurring = val
            event.endDate = undefined
            recurring = !val ? '7,day' : recurring
          }
        "
      />

      <div v-if="isRecurring">
        <dropdown
          v-model="recurring"
          :items="dataRecurring"
          :selected="recurring"
          class="mb-4"
          @on-select="(val) => (recurring = val)"
        />
        <div v-if="recurring === '7,day'" class="mb-4" data-test="weekdays">
          <h2>Op welke dagen?</h2>
          <div class="flex flex-wrap gap-2 mt-2">
            <button
              v-for="day in weekdays"
              :key="day.key"
              type="button"
              class="weekday-chip"
              :class="{
                'weekday-chip-active': isWeekdaySelected(day.key),
                'weekday-chip-locked': day.key === startWeekday
              }"
              :aria-pressed="isWeekdaySelected(day.key) ? 'true' : 'false'"
              :title="
                day.key === startWeekday
                  ? 'De dag van de startdatum staat altijd aan'
                  : ''
              "
              :data-test="`weekday-${day.key}`"
              @click="toggleWeekday(day.key)"
            >
              {{ day.label }}
            </button>
          </div>
        </div>
        <h2>Tot en met</h2>
        <div class="relative">
          <datetime
            v-model="event.endDate"
            :disabled="!recurring"
            :class="recurring ? 'border-gray-600' : 'border-gray-300'"
            class="theme-green border h-11 rounded-lg p-1 px-4"
            :format="dateFormat"
            input-class="w-100 h-100 text-left text-sm disabled:bg-white pl-6"
            :min-datetime="minEndDate"
            :max-datetime="maxEndDate"
            :minute-step="15"
            :phrases="{ ok: 'Ok', cancel: 'Annuleer' }"
          />
          <icon-base class="absolute left-3 top-3 text-gray-600" size="20">
            <icon-calendar />
          </icon-base>
        </div>
        <p
          class="text-xs mt-2 mb-6"
          :class="endDateTooLate ? 'text-red-600' : 'text-gray-600'"
          data-test="series-limit-hint"
        >
          {{ seriesLimitHint }}
        </p>
      </div>
      <button class="button button-primary w-full" @click="saveEvent">
        Volgende
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import AppBar from '@/components/AppBar'
import Toggle from '@/components/Toggle'
import Dropdown from '@/components/Dropdown'
import IconBase from '@/components/IconBase'
import IconCalendar from '@/components/icons/IconCalendar'
import DateTimePicker from '@/components/DateTimePicker'
import data from '@/utils/data'
import {
  WEEKDAYS,
  effectiveWeekdays,
  formatSeriesDay,
  isEndDateWithinLimit,
  isoWeekday,
  seriesMaxEndDay,
  SUPPORTED_RECURRING
} from '@/utils/recurring'
import { EVENT_ITEM_SET_WHEN } from '@/store/actions/event'
import analytics from '@/utils/analytics'

export default {
  name: 'OrganizeWhen',
  components: {
    AppBar,
    DateTimePicker,
    Toggle,
    Dropdown,
    IconBase,
    IconCalendar
  },
  data() {
    return {
      event: {
        startDate: undefined,
        startTime: undefined,
        endDate: undefined,
        endTime: undefined
      },
      dateFormat: DateTime.DATE_HUGE,
      dataRecurring: data.eventRecurringChoices,
      isRecurring: false,
      recurring: '7,day',
      selectedWeekdays: [],
      weekdays: WEEKDAYS,
      origin: this.$route.query.origin
    }
  },
  computed: {
    ...mapState({
      eventTypeList: (state) => state.eventTypes.eventTypeList,
      eventItemEventTypeID: (state) => state.event.eventItem.eventTypeID,
      eventItem: (state) => state.event.eventItem
    }),
    today() {
      return DateTime.local().toString()
    },
    startWeekday() {
      return isoWeekday(this.event.startDate)
    },
    // the start date as emitted by the picker (UTC midnight of that day), so the
    // popup opens on the start day instead of on local midnight, which vue-datetime
    // would store as the day before
    minEndDate() {
      return this.event.startDate || this.today
    },
    maxEndDay() {
      return seriesMaxEndDay(this.event.startDate)
    },
    // ADR 0015: "Tot en met" reaches at most start + 3 calendar months
    maxEndDate() {
      return this.maxEndDay ? this.maxEndDay.endOf('day').toISO() : null
    },
    maxEndDateLabel() {
      return this.maxEndDay ? formatSeriesDay(this.maxEndDay) : null
    },
    seriesLimitHint() {
      const until = this.maxEndDateLabel
        ? ` (tot en met ${this.maxEndDateLabel})`
        : ''
      return `Maximaal 3 maanden doorlopend${until}. Je kunt de reeks later verlengen.`
    },
    endDateTooLate() {
      return !isEndDateWithinLimit(this.event.startDate, this.event.endDate)
    }
  },
  created() {
    if (!this.hasAccessToScreen()) {
      this.$router.push(`/events/new`)
      return
    }
    this.event = { ...this.eventItem }
    this.isRecurring = this.eventItem.isRecurring
    // drafts saved before ADR 0015 may hold a dropped frequency (6,month, 1,year)
    this.recurring = SUPPORTED_RECURRING.includes(this.eventItem.recurring)
      ? this.eventItem.recurring
      : '7,day'
    this.selectedWeekdays = Array.isArray(this.eventItem.recurringWeekdays)
      ? [...this.eventItem.recurringWeekdays]
      : []
    analytics.capture('create_activity_when_viewed')
  },
  methods: {
    hasAccessToScreen() {
      let hasAccess = false
      if (this.$store.state.event.eventItem.activityID !== null) {
        hasAccess = true
      }
      return hasAccess
    },
    saveEvent: function () {
      if (this.isValid()) {
        this.event.isRecurring = this.isRecurring
        this.event.recurring = this.isRecurring ? this.recurring : null
        this.event.recurringWeekdays =
          this.isRecurring && this.recurring === '7,day'
            ? effectiveWeekdays(this.selectedWeekdays, this.event.startDate)
            : null
        this.$store.commit(EVENT_ITEM_SET_WHEN, this.event)
        analytics.capture('create_activity_when_completed', {
          has_date: !!this.event.startDate,
          recurring: this.isRecurring
        })
        this.$router.push(
          `/events/new/location${this.origin ? `?origin=${this.origin}` : ''}`
        )
      }
    },
    isValid: function () {
      let isValid = true
      let message = null

      // Check fields are valid
      if (!this.event.startDate) {
        isValid = false
        message = 'Datum is verplicht'
      } else if (!this.event.startTime) {
        isValid = false
        message = 'Starttijd is verplicht'
      } else if (!this.event.endTime) {
        isValid = false
        message = 'Eindtijd is verplicht'
      } else if (
        DateTime.fromISO(this.event.endTime) <
        DateTime.fromISO(this.event.startTime)
      ) {
        isValid = false
        message = 'Eindtijd kan niet voor begintijd liggen'
      } else if (this.isRecurring) {
        if (!this.recurring) {
          isValid = false
          message = 'Terugkerend is verplicht'
        } else if (!this.event.endDate) {
          isValid = false
          message = 'Einddatum is verplicht'
        } else if (
          DateTime.fromISO(this.event.endDate) <
          DateTime.fromISO(this.event.startDate)
        ) {
          isValid = false
          message = 'Einddatum kan niet voor begindatum liggen'
        } else if (this.endDateTooLate) {
          isValid = false
          message = `Maximaal 3 maanden doorlopend: kies uiterlijk ${this.maxEndDateLabel}`
        }
      }

      if (message !== null) {
        alert(message)
      }

      return isValid
    },
    changeDateTime(dateTimeObj) {
      this.event.startDate = dateTimeObj.dateTime
      this.event.startTime = dateTimeObj.startDateTime
      this.event.endTime = dateTimeObj.endDateTime
    },
    toggleRecurring(val) {
      this.eventIsRecurring = val
    },
    isWeekdaySelected(day) {
      return day === this.startWeekday || this.selectedWeekdays.includes(day)
    },
    toggleWeekday(day) {
      // the weekday of the start date is always part of the series
      if (day === this.startWeekday) return
      this.selectedWeekdays = this.selectedWeekdays.includes(day)
        ? this.selectedWeekdays.filter((d) => d !== day)
        : [...this.selectedWeekdays, day]
    }
  }
}
</script>

<style lang="scss" scoped>
.weekday-chip {
  min-width: 2.75rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  background: #fff;
  color: #374151;
  font-size: 0.875rem;
}
.weekday-chip-active {
  background: #059669;
  border-color: #059669;
  color: #fff;
}
.weekday-chip-locked {
  cursor: default;
  opacity: 0.85;
}
</style>
